import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Version = {
  id: string;
  version_label: string;
  effective_from: string;
  effective_to?: string | null;
  status: string;
  notes?: string | null;
  record_version: number;
};

type Framework = {
  id: string;
  framework_code: string;
  name: string;
  kind: string;
  authority_name?: string | null;
  is_readiness_only: boolean;
  description?: string | null;
  status: string;
  record_version: number;
  versions: Version[];
};

export default function StandardsDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.standards.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-standard-framework', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Framework>(`/education/standards/frameworks/${id}`)).data,
  });
  const row = query.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-standard-framework', id] });
    queryClient.invalidateQueries({ queryKey: ['education-standards'] });
  };

  const setFrameworkStatus = useMutation({
    mutationFn: (status: string) =>
      api.post(`/education/standards/frameworks/${id}/status`, {
        status,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('standardFrameworkStatusUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addVersion = useMutation({
    mutationFn: (values: {
      version_label: string;
      effective_from: dayjs.Dayjs;
      effective_to?: dayjs.Dayjs;
      notes?: string;
    }) =>
      api.post(`/education/standards/frameworks/${id}/versions`, {
        version_label: values.version_label,
        effective_from: values.effective_from.format('YYYY-MM-DD'),
        effective_to: values.effective_to ? values.effective_to.format('YYYY-MM-DD') : undefined,
        notes: values.notes,
      }),
    onSuccess: () => {
      message.success(t('standardVersionCreated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const setVersionStatus = useMutation({
    mutationFn: ({ versionId, status, record_version }: { versionId: string; status: string; record_version: number }) =>
      api.post(`/education/standards/versions/${versionId}/status`, { status, record_version }),
    onSuccess: () => {
      message.success(t('standardVersionStatusUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.framework_code || t('standards')}
      description={t('standardsIntro')}
      tags={
        row ? (
          <Space>
            <Tag>{row.status}</Tag>
            {row.is_readiness_only ? <Tag color="orange">{t('readinessOnly')}</Tag> : null}
          </Space>
        ) : null
      }
      extra={<Button onClick={() => navigate('/education/standards')}>{t('standards')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('standardsReadinessHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('name')}>{row.name}</Descriptions.Item>
              <Descriptions.Item label={t('frameworkKind')}>{row.kind}</Descriptions.Item>
              <Descriptions.Item label={t('authorityName')}>{row.authority_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('detail')} span={2}>
                {row.description || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>

        {canManage && row?.status === 'active' ? (
          <Button danger loading={setFrameworkStatus.isPending} onClick={() => setFrameworkStatus.mutate('retired')}>
            {t('retireFramework')}
          </Button>
        ) : null}
        {canManage && row?.status === 'retired' ? (
          <Button loading={setFrameworkStatus.isPending} onClick={() => setFrameworkStatus.mutate('active')}>
            {t('reactivateFramework')}
          </Button>
        ) : null}

        <Card size="small" title={t('standardVersions')}>
          {canManage && row?.status === 'active' ? (
            <Form
              layout="vertical"
              style={{ maxWidth: 560, marginBottom: 16 }}
              initialValues={{ effective_from: dayjs() }}
              onFinish={(values) => addVersion.mutate(values)}
            >
              <Form.Item name="version_label" label={t('versionLabel')} rules={[{ required: true }]}>
                <Input maxLength={40} placeholder="2026.2" />
              </Form.Item>
              <Form.Item name="effective_from" label={t('effectiveFrom')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="effective_to" label={t('effectiveTo')}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="notes" label={t('standardNotes')}>
                <Input.TextArea rows={2} maxLength={2000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addVersion.isPending}>
                {t('addVersion')}
              </Button>
            </Form>
          ) : null}
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={row?.versions || []}
            columns={[
              { title: t('versionLabel'), dataIndex: 'version_label', width: 120 },
              { title: t('effectiveFrom'), dataIndex: 'effective_from', width: 120 },
              { title: t('effectiveTo'), dataIndex: 'effective_to', width: 120, render: (v) => v || '—' },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v: string) => <Tag color={v === 'active' ? 'green' : undefined}>{v}</Tag>,
              },
              { title: t('standardNotes'), dataIndex: 'notes', ellipsis: true },
              {
                title: t('actions'),
                width: 200,
                render: (_: unknown, ver: Version) =>
                  canManage && ver.status !== 'superseded' ? (
                    <Select
                      size="small"
                      style={{ minWidth: 160 }}
                      placeholder={t('status')}
                      options={[
                        ...(ver.status !== 'active' ? [{ value: 'active', label: t('activateVersion') }] : []),
                        ...(ver.status === 'draft' ? [{ value: 'draft', label: t('statusDraft') }] : []),
                        { value: 'superseded', label: t('supersedeVersion') },
                      ].filter((o, i, arr) => arr.findIndex((x) => x.value === o.value) === i)}
                      onChange={(status) =>
                        setVersionStatus.mutate({
                          versionId: ver.id,
                          status,
                          record_version: ver.record_version,
                        })
                      }
                    />
                  ) : null,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
